import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import PricingSection from "@/components/PricingSection";
import ScoreGauge from "@/components/ScoreGauge";
import api from "@/lib/api";
import { track, getSessionId } from "@/lib/events";
import { useSeo } from "@/lib/seo";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { PromoVideo } from "@/components/PromoVideo";
import { toast } from "sonner";
import { ArrowRight, Search, Users, Gauge, Unlock, Target, TrendingUp, MessageSquareText, DollarSign, MapPin, ShieldCheck, Check, FileSearch, Bell, Sparkles, Zap, Radar, MessageSquareQuote, Star, X, Loader2, Play } from "lucide-react";

const OWNER_IMG = "https://images.unsplash.com/photo-1753351052363-53ce102830eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxsb2NhbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBzdG9yZSUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NjEzNzk1OHww&ixlib=rb-4.1.0&q=85";
const LOGO = "/aria-logo.png";

const DEFAULT_HERO = {
  headline: "Find the opportunities your competitors are missing.",
  subheadline: "Enter your business and let our AI analyze your market, competitors and online presence to uncover your highest-value growth opportunities.",
  cta: "Analyze My Business Free",
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] } }),
};
const reveal = { viewport: { once: true, margin: "-80px" }, initial: "hidden", whileInView: "show" };

function TestimonialModal({ onClose }) {
  const [f, setF] = useState({ name: "", role: "", company: "", quote: "", rating: 5, email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (f.name.trim().length < 2) return toast.error("Please add your name");
    if (f.quote.trim().length < 20) return toast.error("Please write at least a sentence (20+ characters)");
    setSubmitting(true);
    try {
      await api.post("/testimonials", f);
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not submit — please try again");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm" data-testid="testimonial-modal" onClick={onClose}>
      <div className="mt-10 w-full max-w-lg rounded-2xl border border-black/10 bg-white p-6 shadow-2xl sm:p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2"><MessageSquareQuote size={16} className="text-klein" /><span className="mono-label">Share your A.R.I.A. story</span></div>
          <button onClick={onClose} data-testid="testimonial-close" className="text-slate-400 hover:text-brand-dark"><X size={18} /></button>
        </div>
        {done ? (
          <div className="py-10 text-center" data-testid="testimonial-success">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success/10 text-success"><Check size={24} /></div>
            <h3 className="mt-4 font-display text-xl">Thank you!</h3>
            <p className="mt-2 text-sm text-slate-600">Your testimonial was submitted and is pending review. We appreciate you sharing your experience.</p>
            <button onClick={onClose} className="mt-6 rounded-full bg-brand-dark px-5 py-2.5 text-sm font-medium text-white hover:bg-klein">Close</button>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div>
              <label className="mono-label !text-slate-400">Your rating</label>
              <div className="mt-1.5 flex gap-1" data-testid="testimonial-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" data-testid={`rating-${n}`} onClick={() => set("rating", n)} className={n <= f.rating ? "text-klein" : "text-slate-300"}>
                    <Star size={26} fill="currentColor" />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mono-label !text-slate-400">Your testimonial *</label>
              <textarea data-testid="testimonial-quote" rows={4} value={f.quote} onChange={(e) => set("quote", e.target.value)} maxLength={600}
                placeholder="What did A.R.I.A. help you achieve?" className="mt-1.5 w-full resize-none rounded-lg border border-black/15 bg-white px-3 py-2 text-sm focus:border-klein focus:outline-none" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="mono-label !text-slate-400">Name *</label><input data-testid="testimonial-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" className="mt-1.5 w-full rounded-lg border border-black/15 px-3 py-2 text-sm focus:border-klein focus:outline-none" /></div>
              <div><label className="mono-label !text-slate-400">Role</label><input data-testid="testimonial-role" value={f.role} onChange={(e) => set("role", e.target.value)} placeholder="Owner" className="mt-1.5 w-full rounded-lg border border-black/15 px-3 py-2 text-sm focus:border-klein focus:outline-none" /></div>
              <div><label className="mono-label !text-slate-400">Company</label><input data-testid="testimonial-company" value={f.company} onChange={(e) => set("company", e.target.value)} placeholder="Acme HVAC" className="mt-1.5 w-full rounded-lg border border-black/15 px-3 py-2 text-sm focus:border-klein focus:outline-none" /></div>
              <div><label className="mono-label !text-slate-400">Email (private)</label><input data-testid="testimonial-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" className="mt-1.5 w-full rounded-lg border border-black/15 px-3 py-2 text-sm focus:border-klein focus:outline-none" /></div>
            </div>
            <p className="text-xs text-slate-400">Submissions are reviewed before appearing on the site. Your email is never published.</p>
            <button data-testid="testimonial-submit" onClick={submit} disabled={submitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-dark px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-klein disabled:opacity-50">
              {submitting ? <><Loader2 size={15} className="animate-spin" /> Submitting…</> : <>Submit testimonial</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Landing() {
  useSeo({
    title: "A.R.I.A. — AI competitive analysis & 30-day growth plan for local businesses",
    description: "A.R.I.A. analyzes your website, market and competitors, then hands you a prioritized, evidence-backed 30-day growth plan you can execute — or let it execute for you.",
    path: "/",
  });
  const [url, setUrl] = useState("");
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [testimonials, setTestimonials] = useState([]);
  const [showTst, setShowTst] = useState(false);
  const [sugs, setSugs] = useState([]);
  const [acOpen, setAcOpen] = useState(false);
  const [acLoading, setAcLoading] = useState(false);
  const acTimer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (acTimer.current) clearTimeout(acTimer.current);
    const q = url.trim();
    if (q.length < 3) { setSugs([]); setAcOpen(false); return; }
    acTimer.current = setTimeout(async () => {
      setAcLoading(true);
      try {
        const { data } = await api.post("/places/autocomplete", { input: q });
        setSugs(data.suggestions || []);
        setAcOpen((data.suggestions || []).length > 0);
      } catch { /* silent — typing a URL still works */ } finally { setAcLoading(false); }
    }, 320);
    return () => acTimer.current && clearTimeout(acTimer.current);
  }, [url]);

  useEffect(() => {
    api.get(`/landing/variant?session_id=${encodeURIComponent(getSessionId())}`)
      .then(({ data }) => {
        if (data?.content) setHero(data.content);
        localStorage.setItem("oa_landing_variant", data?.variant || "control");
        localStorage.setItem("oa_landing_exp", data?.experiment_id || "");
        track("landing_view", { landing_variant: data?.variant });
      })
      .catch(() => track("landing_view"));
    api.get("/testimonials?limit=6").then(({ data }) => setTestimonials(data.testimonials || [])).catch(() => {});
  }, []);

  const start = () => {
    const v = url.trim();
    const looksLikeUrl = /^[^\s]+\.[a-z]{2,}/i.test(v);
    navigate("/analyze", { state: looksLikeUrl ? { website: v } : { business_name: v } });
  };

  const pickPlace = async (s) => {
    setAcOpen(false); setUrl(s.main);
    try {
      const { data } = await api.get(`/places/details/${s.place_id}`);
      const p = data.place;
      track("landing_place_picked");
      navigate("/analyze", {
        state: {
          website: (p.website || "").replace(/^https?:\/\//, "").split("?")[0],
          business_name: p.name, place: p,
        },
      });
    } catch {
      navigate("/analyze", { state: { business_name: s.main } });
    }
  };

  const steps = [
    { icon: Search, t: "Enter your business", d: "Add your website and a few basics. Takes under a minute." },
    { icon: Users, t: "AI researches your landscape", d: "Our agents analyze your positioning and relevant competitors." },
    { icon: Gauge, t: "Receive your Growth Score", d: "A 0–100 diagnostic across six growth dimensions." },
    { icon: Unlock, t: "Unlock your growth report", d: "Get 10 prioritized opportunities and a 30-day action plan." },
  ];

  const categories = [
    { icon: TrendingUp, t: "Customer Acquisition" }, { icon: Search, t: "SEO & Content" },
    { icon: Target, t: "Offers & Positioning" }, { icon: DollarSign, t: "Pricing Presentation" },
    { icon: Gauge, t: "Conversion Optimization" }, { icon: MessageSquareText, t: "Reputation & Reviews" },
    { icon: MapPin, t: "Local Marketing" }, { icon: ShieldCheck, t: "Competitive Differentiation" },
  ];

  const faqs = [
    ["Is the free scan really free?", "Yes. The free scan gives you a real business summary, up to 3 detected competitors, your Growth Score and 3 opportunity previews — no payment required."],
    ["How is the Growth Score calculated?", "It is an AI-generated diagnostic indicator across six categories. It is a directional guide to where growth potential exists, not an objective business valuation."],
    ["Do you guarantee results or revenue?", "No. We surface concrete, evidence-based opportunities and prioritize them. We never make guaranteed income or ranking promises."],
    ["Where does competitor data come from?", "Our agents analyze publicly available information. When something is uncertain or inferred, we clearly mark it as such and never fabricate prices or reviews."],
    ["What is the Opportunity Monitor?", "A $32.50/month service where the AI re-analyzes your market weekly and emails you a report on competitor changes, new offers, threats and fresh opportunities. Cancel anytime."],
  ];

  const capabilities = ["Competitor scorecards", "SWOT quadrant", "Positioning matrix", "Evidence & citations", "Weekly monitoring", "Fix-this-for-me assets", "Growth signals", "30-day action plan"];

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 lg:pb-0">
      <Navbar />
      {showTst && <TestimonialModal onClose={() => setShowTst(false)} />}

      {/* Sticky mobile CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 p-3 backdrop-blur lg:hidden" data-testid="sticky-mobile-cta">
        <button data-testid="sticky-analyze-btn" onClick={start}
          className="flex h-12 w-full items-center justify-center rounded-full bg-brand-dark text-base font-semibold text-white">
          Analyze My Business Free <ArrowRight size={18} className="ml-2" />
        </button>
      </div>

      {/* ===================== PROMO VIDEO ===================== */}
      <PromoVideo />

      {/* ===================== HERO (dark, cinematic) ===================== */}
      <section className="noise relative overflow-hidden bg-brand-dark text-white">
        <div className="dot-grid absolute inset-0 opacity-70" />
        <div className="absolute -right-40 -top-48 h-[42rem] w-[42rem] rounded-full bg-klein/25 blur-[140px]" />
        <div className="absolute -left-32 top-1/3 h-[30rem] w-[30rem] rounded-full bg-cyan/10 blur-[130px]" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-brand-dark" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 py-16 sm:py-20 lg:grid-cols-12 lg:px-8 lg:py-28">
          <motion.div className="lg:col-span-7" initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.08 } } }}>
            <motion.img variants={fade} src={LOGO} alt="A.R.I.A. — Autonomous Research, Intelligence & Analytics"
              className="mb-4 h-36 w-auto sm:h-44 lg:h-52" data-testid="hero-logo" />
            <motion.h1 variants={fade} data-testid="hero-headline" className="mt-6 font-display text-4xl font-semibold leading-[1.04] tracking-tight sm:text-6xl lg:text-[4.4rem]">
              {hero.headline.includes("missing") ? (
                <>{hero.headline.replace(/missing\.?$/, "")}<span className="text-gradient">missing.</span></>
              ) : hero.headline}
            </motion.h1>
            <motion.p variants={fade} className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
              {hero.subheadline}
            </motion.p>

            {/* glass analyze bar — Google business search */}
            <motion.div variants={fade} className="relative mt-9 max-w-xl">
              <div className="flex flex-col items-stretch gap-2 rounded-2xl border border-white/12 bg-white/[0.05] p-2 backdrop-blur-md transition-colors focus-within:border-cyan/60 sm:flex-row sm:items-center sm:rounded-full sm:pl-5">
                <div className="hidden items-center text-slate-400 sm:flex"><Search size={18} /></div>
                <input data-testid="hero-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
                  onFocus={() => sugs.length && setAcOpen(true)}
                  onKeyDown={(e) => e.key === "Enter" && start()} placeholder="Your business name or website"
                  aria-label="Find your business" autoComplete="off"
                  className="min-w-0 flex-1 bg-transparent px-3 py-3 text-base text-white placeholder:text-slate-500 focus:outline-none sm:py-0 sm:text-lg" />
                {acLoading && <Loader2 size={16} className="mr-2 hidden animate-spin text-cyan sm:block" />}
                <button data-testid="hero-analyze-btn" onClick={start}
                  className="group inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-klein to-cyan px-6 text-sm font-semibold text-brand-dark transition-all hover:shadow-[0_0_28px_rgba(0,229,255,0.45)]">
                  {hero.cta} <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
              {acOpen && sugs.length > 0 && (
                <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-white/15 bg-brand-dark/95 shadow-2xl backdrop-blur-md" data-testid="hero-place-results">
                  {sugs.map((s) => (
                    <button key={s.place_id} data-testid={`hero-place-result-${s.place_id}`} type="button" onClick={() => pickPlace(s)}
                      className="flex w-full items-start gap-2 border-b border-white/5 px-4 py-3 text-left last:border-0 transition-colors hover:bg-white/10">
                      <MapPin size={14} className="mt-0.5 shrink-0 text-cyan" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-white">{s.main}</div>
                        {s.secondary && <div className="truncate text-xs text-slate-400">{s.secondary}</div>}
                      </div>
                    </button>
                  ))}
                  <div className="px-4 py-2 text-[11px] text-slate-500">Pick your business on Google — we prefill your details and analyze instantly.</div>
                </div>
              )}
            </motion.div>
            <motion.div variants={fade} className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
              <button data-testid="hero-watch-tour"
                onClick={() => document.getElementById("promo-video")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className="group inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.04] px-4 py-2 text-xs font-medium text-white backdrop-blur-sm transition-colors hover:border-cyan/60 hover:bg-white/10">
                <Play size={12} fill="currentColor" className="text-cyan" /> Watch the 2-minute tour
              </button>
              {["No credit card", "Results in under a minute", "Every claim cited"].map((t) => (
                <span key={t} className="flex items-center gap-1.5 text-xs text-slate-400"><Check size={13} className="text-cyan" /> {t}</span>
              ))}
            </motion.div>
          </motion.div>

          {/* floating live report preview */}
          <motion.div className="hidden lg:col-span-5 lg:block" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}>
            <div className="animate-float rounded-2xl border border-white/10 bg-white text-ink shadow-2xl glow-blue">
              <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
                <span className="mono-label">Live Opportunity Report</span>
                <span className="mono-label !text-success">● analyzed</span>
              </div>
              <div className="flex items-center justify-between px-6 pt-6">
                <ScoreGauge score={78} size={150} />
                <div className="text-right"><span className="font-mono-data text-4xl font-semibold text-klein">14</span><p className="mono-label mt-1">opportunities</p></div>
              </div>
              <div className="space-y-2 p-6">
                {[["Launch weekly Google Business posts", "High"], ["Add transparent pricing to service pages", "High"], ["Automate post-job review requests", "Med"]].map(([t, p], i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 bg-[#FAFAFA] px-3 py-2.5 text-sm">
                    <span className="pr-3">{t}</span><span className={`font-mono-data text-xs ${p === "High" ? "text-klein" : "text-warning"}`}>{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* capability marquee */}
        <div className="relative border-t border-white/10 py-5">
          <div className="flex overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_12%,#000_88%,transparent)]">
            <div className="flex shrink-0 animate-marquee items-center gap-3 pr-3">
              {[...capabilities, ...capabilities].map((c, i) => (
                <span key={i} className="flex items-center gap-2 whitespace-nowrap rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs text-slate-400"><Radar size={12} className="text-cyan" /> {c}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== STAT BAND ===================== */}
      <section className="border-b border-slate-200 bg-white">
        <motion.div {...reveal} variants={{ show: { transition: { staggerChildren: 0.1 } } }} className="mx-auto grid max-w-7xl gap-px bg-slate-200 sm:grid-cols-3">
          {[["6", "growth dimensions scored"], ["< 60s", "from URL to full report"], ["100%", "of claims carry a source"]].map(([n, l], i) => (
            <motion.div key={l} variants={fade} custom={i} className="bg-white px-6 py-10 text-center">
              <p className="font-mono-data text-4xl font-semibold text-klein sm:text-5xl">{n}</p>
              <p className="mono-label mt-2">{l}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ===================== SOCIAL PROOF ===================== */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:py-16 lg:px-8">
          <p className="text-center mono-label">Built for operators across every local industry</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-5 sm:gap-x-14">
            {["HVAC", "Dental", "Legal", "Fitness", "Real Estate", "Med Spa", "Auto", "Agencies"].map((n) => (
              <span key={n} className="font-display text-xl font-semibold tracking-tight text-slate-300 transition-colors hover:text-klein sm:text-2xl">{n}</span>
            ))}
          </div>

          {testimonials.length > 0 ? (
            <motion.div {...reveal} variants={{ show: { transition: { staggerChildren: 0.1 } } }} className="mt-14 grid gap-6 md:grid-cols-3">
              {testimonials.map((t) => ({ quote: t.quote, name: t.name, role: [t.role, t.company].filter(Boolean).join(" · "), rating: t.rating })).map((t, i) => (
                <motion.figure key={t.name + i} variants={fade} custom={i} data-testid={`testimonial-card-${i}`}
                  className="relative rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-7">
                  {t.rating ? <div className="flex items-center gap-0.5 text-klein" aria-label={`${t.rating} out of 5`}>{Array.from({ length: t.rating }).map((_, s) => <Star key={s} size={15} fill="currentColor" />)}</div> : null}
                  <span className="font-display text-5xl leading-none text-klein/25">&ldquo;</span>
                  <blockquote className="-mt-4 text-sm leading-relaxed text-slate-700">{t.quote}</blockquote>
                  <figcaption className="mt-5 flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-dark font-mono-data text-xs font-semibold text-cyan">{t.name.split(" ").map((w) => w[0]).join("")}</span>
                    <span><span className="block text-sm font-semibold text-brand-dark">{t.name}</span><span className="mono-label !text-slate-400">{t.role || "Verified customer"}</span></span>
                  </figcaption>
                </motion.figure>
              ))}
            </motion.div>
          ) : (
            <div className="mx-auto mt-12 max-w-xl text-center" data-testid="testimonials-empty">
              <p className="font-display text-xl tracking-tight text-brand-dark">No customer stories published yet</p>
              <p className="mt-2 text-sm text-slate-500">
                We only publish reviews real customers have sent us and we have verified — so this space stays
                empty until then. Used A.R.I.A.? Yours could be the first.
              </p>
            </div>
          )}
          <div className="mt-8 flex flex-col items-center gap-3">
            <button data-testid="share-testimonial-btn" onClick={() => setShowTst(true)}
              className="inline-flex items-center gap-2 rounded-full border border-klein/30 bg-klein/5 px-5 py-2.5 text-sm font-medium text-klein transition-colors hover:bg-klein hover:text-white">
              <MessageSquareQuote size={16} /> Share your story
            </button>
          </div>
        </div>
      </section>

      {/* ===================== PILLARS ===================== */}
      <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 lg:px-8">
        <motion.div {...reveal} variants={{ show: { transition: { staggerChildren: 0.1 } } }} className="grid gap-6 md:grid-cols-3">
          {[
            [FileSearch, "Cited", "Every competitor fact carries its source and timestamp — we never fabricate prices or reviews.", "from-klein/10"],
            [Target, "Prioritized", "The handful of actions that actually move revenue, ranked by impact — not a 40-page data dump.", "from-cyan/10"],
            [Bell, "Repeated", "Weekly monitoring flags competitor moves the moment they happen, so you're never surprised.", "from-success/10"],
          ].map(([Icon, t, d, g], i) => (
            <motion.div key={t} variants={fade} custom={i}
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/60">
              <div className={`absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${g} to-transparent opacity-0 transition-opacity group-hover:opacity-100`} />
              <span className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-brand-dark text-cyan"><Icon size={22} /></span>
              <h3 className="relative mt-5 font-display text-2xl font-semibold tracking-tight">{t}</h3>
              <p className="relative mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ===================== HOW IT WORKS (stepper) ===================== */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:py-24 lg:px-8">
          <span className="mono-label">How it works</span>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-semibold tracking-tight sm:text-4xl">From a URL to a prioritized growth plan</h2>
          <div className="relative mt-14">
            <div className="absolute left-0 right-0 top-6 hidden h-px bg-gradient-to-r from-klein/40 via-cyan/40 to-transparent lg:block" />
            <motion.div {...reveal} variants={{ show: { transition: { staggerChildren: 0.1 } } }} className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((s, i) => (
                <motion.div key={i} variants={fade} custom={i} className="relative">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white font-mono-data text-sm font-semibold text-klein shadow-sm">0{i + 1}</div>
                  <s.icon className="mt-5 text-brand-dark" size={26} />
                  <h3 className="mt-4 text-lg font-semibold">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.d}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===================== EXAMPLE REPORT ===================== */}
      <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <motion.div {...reveal} variants={fade}>
            <span className="mono-label">Example report</span>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">See exactly what you'll get</h2>
            <p className="mt-4 leading-relaxed text-slate-600">Every report separates observed facts from AI inferences and recommendations — so you always know what's evidence and what's analysis.</p>
            <ul className="mt-6 space-y-3">
              {["Executive summary & Growth Score", "Full competitor comparison & biggest gaps", "10 prioritized opportunities with impact & difficulty", "7-day quick wins & 30-day action plan", "Risks, threats and recommended next steps"].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm"><Check size={16} className="mt-0.5 shrink-0 text-klein" />{f}</li>
              ))}
            </ul>
            <button data-testid="example-demo-btn" onClick={() => navigate("/analysis/an_demo_sample")}
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-brand-dark transition-all hover:-translate-y-0.5 hover:border-klein hover:text-klein">
              View live sample report <ArrowRight size={16} />
            </button>
          </motion.div>
          <motion.div {...reveal} variants={fade} custom={1} className="rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-8 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 pb-4">
              <ScoreGauge score={78} size={140} label="Sample" />
              <div className="text-right"><span className="font-mono-data text-3xl font-semibold text-klein">14</span><p className="mono-label mt-1">Opportunities</p></div>
            </div>
            <div className="mt-4 space-y-2">
              {["Launch weekly Google Business posts", "Add transparent pricing to service pages", "Automate post-job review requests"].map((t, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg bg-white p-3 text-sm shadow-sm"><span>{t}</span><span className="font-mono-data text-klein">High</span></div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===================== BENEFITS ===================== */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:py-24 lg:grid-cols-2 lg:items-center lg:px-8">
          <motion.div {...reveal} variants={fade} className="overflow-hidden rounded-2xl border border-slate-200">
            <img src={OWNER_IMG} alt="Business owner" className="h-full w-full object-cover" />
          </motion.div>
          <motion.div {...reveal} variants={fade} custom={1}>
            <span className="mono-label">Why it matters</span>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Compete like you have a full analyst team</h2>
            <div className="mt-8 grid gap-6">
              {[
                [Zap, "Clarity, not guesswork", "Stop wondering what to fix next. Get a ranked list grounded in real competitive evidence."],
                [Target, "Built for your market", "Tuned for the acquisition, reputation and conversion levers that actually move revenue."],
                [Sparkles, "Always-on intelligence", "With monitoring, you're the first to know when a competitor changes their offers or pricing."],
              ].map(([Icon, t, d]) => (
                <div key={t} className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-klein/10 text-klein"><Icon size={18} /></span>
                  <div><h3 className="text-lg font-semibold">{t}</h3><p className="mt-1 text-sm leading-relaxed text-slate-600">{d}</p></div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===================== CATEGORIES (dark bento) ===================== */}
      <section className="noise relative overflow-hidden bg-brand-dark">
        <div className="dot-grid absolute inset-0 opacity-60" />
        <div className="absolute -right-24 top-0 h-80 w-80 rounded-full bg-klein/20 blur-[120px]" />
        <div className="relative mx-auto max-w-7xl px-5 py-16 sm:py-24 lg:px-8">
          <span className="mono-label !text-slate-400">Opportunity categories</span>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Every angle of growth, analyzed</h2>
          <motion.div {...reveal} variants={{ show: { transition: { staggerChildren: 0.05 } } }} className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((c, i) => (
              <motion.div key={c.t} variants={fade} custom={i}
                className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur transition-all hover:-translate-y-1 hover:border-cyan/40 hover:bg-white/[0.06]">
                <c.icon className="text-cyan transition-transform group-hover:scale-110" size={24} />
                <h3 className="mt-4 text-base font-semibold text-white">{c.t}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===================== PRICING ===================== */}
      <section id="pricing" className="mx-auto max-w-7xl px-5 py-16 sm:py-24 lg:px-8">
        <div className="text-center">
          <span className="mono-label">Pricing</span>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Start free. Upgrade when you see the value.</h2>
        </div>
        <div className="mt-12"><PricingSection onFree={() => window.scrollTo({ top: 0, behavior: "smooth" })} /></div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-3xl px-5 py-16 sm:py-24 lg:px-8">
          <span className="mono-label">FAQ</span>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Questions, answered</h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {faqs.map(([q, a], i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-slate-200">
                <AccordionTrigger className="text-left text-base font-medium hover:no-underline">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-klein to-cyan">
        <div className="absolute inset-0 dot-grid opacity-30" />
        <div className="relative mx-auto max-w-4xl px-5 py-16 text-center sm:py-24 lg:px-8">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-5xl">Discover your highest-value opportunities today.</h2>
          <button data-testid="final-cta-btn" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="mt-8 inline-flex h-14 items-center gap-2 rounded-full bg-brand-dark px-10 text-base font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-2xl">
            Analyze My Business Free <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <footer className="bg-brand-dark py-12 text-center">
        <img src={LOGO} alt="A.R.I.A." className="mx-auto h-28 w-auto" />
        <div className="mx-auto mt-6 flex max-w-md justify-center px-5">
          <NewsletterSignup />
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
          <a href="/feedback" data-testid="footer-feedback-link" className="text-slate-300 transition-colors hover:text-white">Feedback</a>
          <a href="/support" className="text-slate-300 transition-colors hover:text-white">Support</a>
          <a href="mailto:Support@ariasanalytics.com" data-testid="footer-contact-email" className="text-slate-300 transition-colors hover:text-white">Support@ariasanalytics.com</a>
        </div>
        <p className="mono-label mt-4 !text-slate-500">AI diagnostics, not financial advice</p>
      </footer>
    </div>
  );
}
